// TODO
// import * as Sentry from '@sentry/react';
import { isNil } from 'ramda';

export const LS_TOKEN = 'token';
export const LS_ROLE = 'role';
export const LS_AUTH = 'auth';

// Central logout function for use across app
export function logoutUser() {
  localStorage.removeItem(LS_TOKEN);
  window.location.href = '/#/login';
}

// Token utilities
export function parseJwt(token) {
  try {
    return JSON.parse(atob(token.split('.')[1]));
  } catch (e) {
    return null;
  }
}

export function isTokenExpired(token) {
  const payload = parseJwt(token);
  if (!payload || !payload.exp) return true;
  return payload.exp * 1000 < Date.now();
}

export function isTokenExpiringSoon(token, thresholdMs = 5 * 60 * 1000) {
  const payload = parseJwt(token);
  if (!payload || !payload.exp) return true;
  return payload.exp * 1000 < Date.now() + thresholdMs;
}

export async function renewToken(apiUrl, oldToken) {
  // Strapi v3/v4 does not have refresh by default, so we just return null
  // If you add a refresh endpoint, implement it here
  return null;
}

const authProvider = ({ REACT_APP_API_URL }) => ({
  // Called when the user attempts to log in
  login: async ({ username, password }) => {
    const request = new Request(`${REACT_APP_API_URL}/auth/local`, {
      method: 'POST',
      body: JSON.stringify({ identifier: username, password }),
      headers: new Headers({
        'Content-Type': 'application/json',
      }),
    });

    const response = await fetch(request);
    if (!response.ok) {
      // You can customize error handling here (e.g., show a notification)
      throw new Error('Network error: Unable to login');
    }
    const { jwt, user } = await response.json();
    // Store the token in localStorage (or another storage mechanism)
    localStorage.setItem(LS_TOKEN, jwt);
    localStorage.setItem(LS_AUTH, JSON.stringify(user));

    // Step 2: Use the JWT to fetch full user details (including role)
    const meRequest = new Request(`${REACT_APP_API_URL}/user/me`, {
      method: 'GET',
      headers: new Headers({
        'Content-Type': 'application/json',
        Authorization: `Bearer ${jwt}`,
      }),
    });
    const meResponse = await fetch(meRequest);
    if (!meResponse.ok) {
      throw new Error('Network error: Unable to fetch user data');
    }
    const userData = await meResponse.json();

    const role = userData?.role?.type;

    if (isNil(role)) {
      localStorage.removeItem(LS_TOKEN);
      localStorage.removeItem(LS_AUTH);
      throw new Error('Network error: Unable to fetch');
    }

    localStorage.setItem(LS_ROLE, role);

    return Promise.resolve();
  },

  // Called when the user clicks on the logout button
  logout: () => {
    logoutUser();
    return Promise.resolve();
  },

  // Called when the API returns an error (e.g., 401, 403) to check if it should logout the user
  checkError: (error) => {
    const status = error.status;
    if (status === 401 || status === 403) {
      logoutUser();
      return Promise.reject();
    }
    return Promise.resolve();
  },

  // Called when the user navigates to a new location to check for authentication
  checkAuth: () => {
    const token = localStorage.getItem(LS_TOKEN);
    if (!token || isTokenExpired(token)) {
      logoutUser();
      return Promise.reject();
    }
    if (isTokenExpiringSoon(token)) {
      // Optionally: try to renew token here if implemented
      // For now, treat as valid until expired
    }
    return Promise.resolve();
  },

  // Called when the user navigates to a new location to check for permissions/roles
  // Adjust this if you have role-based permissions in your Strapi app
  getPermissions: () => {
    const role = localStorage.getItem(LS_ROLE);
    return role ? Promise.resolve(role) : Promise.reject(new Error('ErrorGetPermissions'));
  },
  getIdentity: () => {
    try {
      const { id, username, email } = JSON.parse(localStorage.getItem(LS_AUTH));
      return Promise.resolve({ id, username, email });
    } catch (error) {
      return Promise.reject(error);
    }
  },
});

export default authProvider;
